import apiClient from '../../apiClient';
import { ErrorType } from '../../commonType';
import { NavigationSteps } from '../../components/OfferDetails/OfferNavigation';
import { createError, NO_OFFER } from '../../util';
import { PIN } from '../../util/strings';
import { removeSpecialCharsFromString } from '../../util/validation';
import { Offer, OfferDetails, OfferResponse, State } from './types';

const OFFER_BY_LOC_API_END_POINT = `${process.env.REACT_APP_API_DOMAIN}${process.env.REACT_APP_OFFER_BY_LOC_API_PATH}`;
const OFFER_BY_PIN_API_END_POINT = `${process.env.REACT_APP_API_DOMAIN}${process.env.REACT_APP_OFFER_BY_PIN_API_PATH}`;

const fetchOfferData = async (
  pin: string,
  mode: string,
  alternateLOCFlag: string | undefined = undefined,
  effortCode: string | undefined = undefined,
): Promise<OfferDetails> => {
  let returnValue: OfferDetails = {
    hasError: false,
    loading: false,
    showProspect: false,
    hasOffers: false,
    currentNavigationStep: NavigationSteps.OFEFR_DETAILS,
    displayFlipOffers: false,
    isPackageSold: false,
  };
  try {
    let isPinSearch = mode === PIN;
    let locAPIEndPoint = '';

    isPinSearch = alternateLOCFlag ? false : isPinSearch;
    if (!isPinSearch) {
      locAPIEndPoint = effortCode
        ? `${OFFER_BY_LOC_API_END_POINT}${pin}?EFC=${effortCode}`
        : `${OFFER_BY_LOC_API_END_POINT}${pin}`;
    }

    const primaryOfferLocationCollection: State[] = [];
    const flipOfferLocationCollection: State[] = [];
    const resp = isPinSearch
      ? await apiClient.get<OfferResponse>(`${OFFER_BY_PIN_API_END_POINT}${pin}`)
      : await apiClient.get<OfferResponse>(locAPIEndPoint);
    const isPackageSold = resp.packageSold && resp.packageSold.toLocaleLowerCase() === 'y';
    if (isPackageSold || (resp.leadId && resp.leadId.length > 0)) {
      returnValue.isPackageSold = true;
      returnValue.soldPackageInformation = {
        destination: resp.destination,
        lastName: resp.lastName,
        firstName: resp.firstName,
        leadId: resp.leadId,
        loc: resp.loc,
        locDescription: resp.locDescription,
        address: resp.address,
        city: resp.city,
        zip: resp.zip,
        state: resp.state,
        totalAmount: resp.totalAmount ? resp.totalAmount : '',
        has3AError: resp.errorMessage && resp.errorMessage.length > 0 ? true : false,
        soldPackageStatusDescription: resp.errorMessage,
        arrivalDate: resp.arrivalDate,
        departureDate: resp.departureDate,
      };
      return returnValue;
    }

    const offers = resp.offers;
    if (offers && offers.length > 0) {
      const primaryOffer = offers.filter((e) => !e.isFlip);
      offers.forEach((e) => {
        e.includeOffer = true;
        if (e.premiums && e.premiums.length > 0) {
          const temp: string[] = [];
          e.premiums.forEach((x) => {
            if (x !== null) {
              let premium = x.replace('<p>', '');
              premium = premium.replace('</p>', '');
              if (x.indexOf('BonvoyTM') > 0) {
                premium = premium.replace('BonvoyTM', 'Bonvoy<sup>TM</sup>');
              }
              temp.push(premium);
            }
          });
          e.premiums = temp;
        }

        if (e.roomTypes && e.roomTypes.length > 0) {
          const rooms = e.roomTypes.filter((x) => x.sequenceNumber === 1);
          const primaryRoom = rooms && rooms.length > 0 ? rooms[0] : e.roomTypes[0];
          e.offerDays = primaryRoom.numberOfDays;
          e.minimumStartingCost = primaryRoom.startingCost;
          e.offerNights = primaryRoom.numberOfNights;
          e.offerMaxGuest = primaryRoom.sleepCapacity;
          e.offerDescription = primaryRoom.roomTypeDescription;
          e.property = primaryRoom.property;
          if (e.property && e.property.stateProvince && e.property.stateProvince) {
            const stateInfo: State = {
              stateCode: e.property?.stateCode,
              stateName: e.property?.stateProvince,
            };
            e.offerLocation = stateInfo;
            if (e.isFlip) {
              flipOfferLocationCollection.push(e.offerLocation);
            } else {
              primaryOfferLocationCollection.push(e.offerLocation);
            }
          }
        }
      });
      if (primaryOffer && primaryOffer.length > 0) {
        const flipOffers: Offer[] = offers.filter((e) => e.isFlip);

        returnValue.hasOffers = true;
        returnValue.primaryOffer = primaryOffer;
        returnValue.flipOffers = flipOffers;
        const currentOffer = primaryOffer[0];
        returnValue.currentOffer = currentOffer;
        returnValue.locDescription = currentOffer.locDescription;
        if (alternateLOCFlag) {
          returnValue.alternateLOCDescription = currentOffer.locDescription;
        }
        returnValue.property = currentOffer.property;
        returnValue.primaryOfferLocations = getDistinctState(primaryOfferLocationCollection);
        returnValue.flipOfferLocations = getDistinctState(flipOfferLocationCollection);
      } else {
        returnValue.hasOffers = false;
      }
      returnValue.hasError = false;
    } else {
      returnValue.hasOffers = false;
      returnValue.hasError = true;
      returnValue.error = { errorType: ErrorType.Error, errorMessageKey: NO_OFFER };
    }
    if (alternateLOCFlag === undefined) {
      if (resp.prospect) {
        returnValue.prospect = {
          addressLine1: resp.prospect?.addressLine1,
          addressLine2: resp.prospect?.addressLine2,
          city: resp.prospect?.city,
          countryCode: resp.prospect?.countryCode,
          email: resp.prospect?.email,
          firstName: removeSpecialCharsFromString(resp.prospect?.firstName),
          lastName: removeSpecialCharsFromString(resp.prospect?.lastName),
          phone: resp.prospect?.phone,
          postalCode: resp.prospect?.postalCode,
          stateProvince: resp.prospect?.stateProvince,
          unitNo: resp.prospect?.unitNo,
        };
      }
      returnValue.showProspect = isPinSearch;
      returnValue.searchMode = mode;
      returnValue.searchParameter = { mode, pin };
    }
  } catch (e: any) {
    returnValue.hasError = true;
    returnValue.tokenExpired = e.response && e.response.tokenExpired && e.response.tokenExpired === true ? true : false;
    if (e.response && e.response.data && e.response.data.errorCode) {
      const {
        response: {
          data: { errorCode },
        },
      } = e;
      returnValue.error = createError(errorCode);
    } else {
      returnValue.error = createError('');
    }
  }
  returnValue.loading = false;
  return returnValue;
};

const getDistinctState = (stateList: State[]): State[] => {
  const distinctStateCodeList = Array.from(new Set(stateList.map((s) => s.stateCode)));
  const states: State[] = distinctStateCodeList.map((code) => {
    return {
      stateCode: code,
      stateName: stateList.find((e) => e.stateCode === code)?.stateName,
    };
  });
  return states;
};

export default fetchOfferData;
